import { fg } from '@atlaskit/platform-feature-flags';
import { useEffect, useState } from 'react';
import { isIsolatedCloud } from '@atlassian/atl-context';

/**
 * this hook requests / refresh / ensure the signed cookies for IC Forge CDN.
 * It is used only within IC environment, and the aim is to allow IC apps to be accessible.
 *
 * @return boolean - whether the signed cookie is still loading (should wait for it before loading the iframe)
 */
export const useCDNAuthSignedCookie = (): boolean => {
	const [isLoading, setIsLoading] = useState(shouldUseCDNAuth());

	useEffect(() => {
		let cancelled = false;

		if (isLoading) {
			loadCDNAuthSignedCookie()
				.catch((err) => {
					// eslint-disable-next-line no-console
					console.error('Error loading CDN auth signed cookie:', err);
				})
				.finally(() => {
					if (!cancelled) {
						setIsLoading(false);
					}
				});
		}

		return () => {
			cancelled = true;
		};
	}, [isLoading]);

	return isLoading;
};

const MAX_SIGNED_COOKIE_AGE_MS = 30 * 60 * 1000; // 30 minutes
let lastSignedCookieFetchTime = 0;
let loadingPromise: Promise<void> | null = null;

const doLoadCDNAuthSignedCookie = async () => {
	// Step 1: Get the signed cookies URL from our backend -> session token based auth
	const signedCookiesUrlResponse = await fetch('/gateway/api/forge-cdn/signed-cookies-url');
	if (!signedCookiesUrlResponse.ok) {
		throw new Error(`Failed to get signed cookies URL: ${signedCookiesUrlResponse.statusText}`);
	}

	// Step 2: Fetch the signed cookies using the obtained URL (with signed URL) -> this will set the cookies in the browser
	const { data: signedCookiesUrl } = await signedCookiesUrlResponse.json();
	if (!signedCookiesUrl || typeof signedCookiesUrl !== 'string') {
		throw new Error('Invalid signed cookies URL received from backend');
	}

	const response = await fetch(signedCookiesUrl, {
		method: 'GET',
		credentials: 'include',
		headers: {
			'Content-Type': 'application/json',
		},
	});
	if (!response.ok) {
		throw new Error(`Failed to fetch signed cookies: ${response.statusText}`);
	}
};

const loadCDNAuthSignedCookie = async () => {
	if (Date.now() - lastSignedCookieFetchTime < MAX_SIGNED_COOKIE_AGE_MS) {
		return;
	}
	// this is to ensure only one loadingPromise is active at a time (to avoid multiple simultaneous requests)
	if (loadingPromise) {
		await loadingPromise;
	} else {
		loadingPromise = (async () => {
			try {
				await doLoadCDNAuthSignedCookie();
				lastSignedCookieFetchTime = Date.now();
			} finally {
				loadingPromise = null;
			}
		})();

		await loadingPromise;
	}
};

export const shouldUseCDNAuth = (): boolean => {
	return fg('forge-ui-cdn-auth') && isIsolatedCloud();
};

// this is only used in tests to reset the cache
export const resetSignedCookieFetchCache = (): void => {
	lastSignedCookieFetchTime = 0;
	loadingPromise = null;
};
